import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { keyframes } from "@emotion/core";
import styled from "@emotion/styled";
import { darken } from "polished";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTimes as DeleteIcon
} from "react-icons/fa";

export const IconButton = ({
    icon,
    negativePaddingSpace = false,
    padding = "0.375em",
    onClick,
    ...props
  }) => (
    <TransparentButton
      {...props}
      padding={padding}
      negativePaddingSpace={negativePaddingSpace}
      onClick={onClick}
    >
      {icon}
    </TransparentButton>
  );

  const TransparentButton = styled.button`
    background-color: transparent;
    border: none;
    padding: ${p => p.padding};
    margin: ${p => (p.negativePaddingSpace ? `-${p.padding}` : undefined)};
    font-size: inherit;
    color: inherit;
    cursor: pointer;
    > svg {
        transform: translateY(1px);
    }
    :disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

/** User Guidance component with multiple display options.
 *
 * @param {Object}                               props
 * @param {"xs"|"sm"|"md"}                       [props.bgIconSize]
 * @param {"sm"|"md"|"lg"|"xl"}                  [props.buttonSize]
 * @param {string}                               [props.className]
 * @param {JSX.Element}                          props.content
 * @param {"inline"|"dialog"|"toast"|"tooltip"}  props.displayMode
 * @param {"left"|"right"}                       [props.from]
 * @param {string}                               [props.heading]
 * @param {(iconProps) => JSX.Element}           [props.icon]
 * @param {"info"|"success"|"warning"|"problem"} props.type
 * @param {string}                               [props.width]
 * @param {bool}                                 [props.noTab]        removes icon from being tabbed to
 */
export function Guidance({
  bgIconSize,
  buttonSize = "md",
  className,
  content,
  displayMode,
  from = "right",
  heading,
  icon = undefined,
  iconFill = undefined,
  type,
  width = "fit-content",
  noTab = false
}) {
  let [
    Icon,
    smallIconColor,
    largeIconColor,
    bgColor,
    borderColor
  ] = getIconAndColorScheme(type, icon);
  let isTooltip = false;

  switch (displayMode) {
    case "inline":
      return (
        <GuidanceBox
          bgColor={bgColor}
          bgIconSize={bgIconSize}
          borderColor={borderColor}
          className={className}
          content={content}
          heading={heading}
          Icon={Icon}
          largeIconColor={largeIconColor}
          width={width}
        />
      );
    case "toast":
      return (
        <GuidanceToast
          bgColor={bgColor}
          bgIconSize={bgIconSize}
          borderColor={borderColor}
          className={className}
          content={content}
          heading={heading}
          Icon={Icon}
          largeIconColor={largeIconColor}
          width={width}
        />
      );
    case "tooltip":
      isTooltip = true;
    // eslint-disable: no-fallthrough
    case "dialog":
      return (
        <GuidanceDialog
          isTooltip={isTooltip}
          bgColor={bgColor}
          bgIconSize={bgIconSize || (isTooltip ? "sm" : undefined)}
          borderColor={borderColor}
          buttonSize={buttonSize}
          className={className}
          content={content}
          from={from}
          heading={heading}
          Icon={Icon}
          iconFill={iconFill}
          largeIconColor={largeIconColor}
          smallIconColor={smallIconColor}
          width={width}
          noTab={noTab}
        />
      );
    default:
      return null;
  }
}

/** For use with Guidance component. This is made available for use externally with the "inline" displayMode Guidance.
 *  Both this component and the "dialog" and "tooltip" displayMode Guidance utilize GuidanceButtonInner.
 *
 * @param {Object}                               props
 * @param {"sm"|"md"|"lg"}                       [props.buttonSize]
 * @param {(iconProps) => JSX.Element}           [props.icon]
 * @param {boolean}                              props.isOpen
 * @param {() => void}                           props.setIsOpen
 * @param {"info"|"success"|"warning"|"problem"} props.type
 */
export function GuidanceButton({
  buttonSize = "md",
  icon,
  isOpen,
  setIsOpen,
  type
}) {
  const { 0: Icon, 1: smallIconColor } = getIconAndColorScheme(type, icon);

  return (
    <GuidanceButtonInner
      buttonSize={buttonSize}
      Icon={Icon}
      iconColor={smallIconColor}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    />
  );
}

function GuidanceBox({
  bgColor,
  bgIconSize,
  borderColor,
  className,
  content,
  from,
  heading,
  Icon,
  isOpen,
  largeIconColor,
  width,
  ...props
}) {
  return (
    <GuidanceBoxTag
      largeIconColor={largeIconColor}
      bgColor={bgColor}
      borderColor={borderColor}
      from={from}
      isOpen={isOpen}
      width={width}
      className={className}
      {...props}
    >
      {/* <LargeIconWrapper bgIconSize={bgIconSize}>
        <Icon color={largeIconColor} />
      </LargeIconWrapper> */}
      <Content>
        {heading && <h4 className="guidance-header">{heading}</h4>}
        {content}
      </Content>
    </GuidanceBoxTag>
  );
}

function GuidanceDialog({
  isTooltip,
  bgColor,
  bgIconSize,
  borderColor,
  buttonSize,
  className,
  content,
  from,
  heading,
  Icon,
  iconFill,
  largeIconColor,
  smallIconColor,
  width,
  noTab
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <RelativeWrapper>
      <GuidanceButtonInner
        buttonSize={buttonSize}
        Icon={Icon}
        iconColor={iconFill || smallIconColor}
        isOpen={isOpen}
        isTooltip={isTooltip}
        setIsOpen={setIsOpen}
        noTab={noTab}
      />
      <Dialog
        bgColor={bgColor}
        bgIconSize={bgIconSize}
        borderColor={borderColor}
        className={className}
        content={content}
        from={from}
        heading={heading}
        Icon={Icon}
        isOpen={isOpen}
        isTooltip={isTooltip}
        largeIconColor={largeIconColor}
        width={width}
      />
      {!isTooltip && (
        <Whiteout onClick={() => setIsOpen(!isOpen)} isOpen={isOpen} />
      )}
    </RelativeWrapper>
  );
}

function GuidanceToast({
  bgColor,
  bgIconSize,
  borderColor,
  className,
  content,
  from,
  heading,
  Icon,
  isOpen,
  largeIconColor,
  width,
  ...props
}) {
  const [exiting, setExiting] = useState(false);
  const toastRef = useRef();
  return (
    <ToastWrapper
      ref={toastRef}
      exiting={exiting}
      largeIconColor={largeIconColor}
    >
      <GuidanceBoxTag
        largeIconColor={largeIconColor}
        bgColor={bgColor}
        borderColor={borderColor}
        from={from}
        isOpen={isOpen}
        width={width}
        className={className}
        {...props}
      >
        <LargeIconWrapper bgIconSize={bgIconSize}>
          <Icon color={largeIconColor} />
        </LargeIconWrapper>
        <Content>
          {heading && <h4 className="guidance-header">{heading}</h4>}
          {content}
        </Content>
        <IconButton
          icon={<DeleteIcon />}
          onClick={() => {
            setExiting(true);
            setTimeout(() => {
              ReactDOM.unmountComponentAtNode(toastRef.current.parentNode);
            }, 250);
          }}
        />
      </GuidanceBoxTag>
    </ToastWrapper>
  );
}

function GuidanceButtonInner({
  buttonSize,
  Icon,
  iconColor,
  isOpen,
  setIsOpen,
  isTooltip = false,
  noTab = false
}) {
  let iconSize;
  switch (buttonSize) {
    case "xl":
      iconSize = "2em";
      break;
    case "lg":
      iconSize = "1.25em";
      break;
    case "md":
      iconSize = "1em";
      break;
    case "sm":
      iconSize = "0.875em";
      break;
    default:
      iconSize = undefined;
  }

  return (
    <GuidanceButtonTag
      fontSize={iconSize}
      isOpen={isOpen}
      onClick={isTooltip ? undefined : () => setIsOpen(!isOpen)}
      onMouseEnter={isTooltip ? () => setIsOpen(true) : undefined}
      onMouseLeave={isTooltip ? () => setIsOpen(false) : undefined}
      title={isTooltip ? undefined : "Click for guidance."}
      tabIndex={noTab ? -1 : 0}
    >
      <Icon color={iconColor} />
    </GuidanceButtonTag>
  );
}

function getIconAndColorScheme(type, icon = undefined) {
  let Icon, smallIconColor, largeIconColor, bgColor, borderColor;
  switch (type) {
    case "info":
      Icon = FaInfoCircle;
      smallIconColor = "#06c";
      largeIconColor = "#8be";
      bgColor = "#def";
      borderColor = "#bcd";
      break;
    case "success":
      Icon = FaCheckCircle;
      smallIconColor = "#093";
      largeIconColor = "#9db";
      bgColor = "#dfe";
      borderColor = "#bdc";
      break;
    case "warning":
      Icon = FaExclamationTriangle;
      smallIconColor = "#f80";
      largeIconColor = "#ed3";
      bgColor = "#ffa";
      borderColor = "#ee3";
      break;
    case "problem":
      Icon = FaExclamationCircle;
      smallIconColor = "#d00";
      largeIconColor = "#f99";
      bgColor = "#ffe3e3";
      borderColor = "#ebb";
      break;
    default:
  }
  if (icon) {
    Icon = icon;
  }
  return [Icon, smallIconColor, largeIconColor, bgColor, borderColor];
}

const boxAppear = keyframes`
  0% { padding: 0 1.5rem; }
  100% { padding: 0.75rem 1.5rem; }
`;

const GuidanceBoxTag = styled.div`
  display: flex;
  width: ${p => p.width};
  padding: 0.5rem;
  background-color: ${p => p.bgColor};
  border: 2px solid ${p => p.borderColor};
  border-radius: 0.5rem;
  animation: ${boxAppear} 0.3s;
`;

const RelativeWrapper = styled.div`
  position: relative;
`;

const GuidanceButtonTag = styled.button`
  cursor: pointer;
  padding: 0.125rem;
  margin: -0.125rem;
  background-color: transparent;
  border: none;
  font-size: ${p => p.fontSize};
  position: relative;
  z-index: ${p => (p.isOpen ? 1401 : undefined)};
  > svg {
    transform: translateY(0.125rem);
  }
`;

const enterTiming = "cubic-bezier(0.39, 0.575, 0.565, 1)";

const Dialog = styled(GuidanceBox)`
  position: absolute;
  top: 75%;
  right: ${p => (p.from === "left" ? undefined : "75%")};
  left: ${p => (p.from === "left" ? "75%" : undefined)};
  z-index: ${p => (p.isOpen ? 1400 : -1)};
  border-top-left-radius: ${p => (p.from === "left" ? 0 : undefined)};
  border-top-right-radius: ${p => (p.from === "right" ? 0 : undefined)};
  opacity: ${p => (p.isOpen ? 1 : 0)};
  transform: ${p =>
    p.isOpen
      ? "translate(0) scale(1)"
      : `translate(${p.from === "left" ? "-" : ""}12.5%, -12.5%) scale(0.75)`};
  transition: opacity 0.15s ${enterTiming}, transform 0.15s ${enterTiming};
`;

const toastAppear = keyframes`
  from {
    opacity: 0;
    transform: translateY(3em);
  }
  to {
    opacity: 1;
  }
`;

const toastExit = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
    transform: translateX(3em);
  }
`;

const ToastWrapper = styled.div`
  position: fixed;
  bottom: 1rem;
  right: 1.5rem;
  z-index: 900;
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px 4px rgba(0, 0, 0, 0.2);
  animation-name: ${p => (p.exiting ? toastExit : toastAppear)};
  animation-duration: 250ms;
  animation-timing-function: ease;
  animation-iteration-count: once;
  > div {
    animation: none;
    > div {
      animation: none;
      margin-bottom: 0.125rem;
      max-height: 21.5rem;
      overflow-y: auto;
      > .guidance-header {
        margin-bottom: 0.375rem;
      }
      .toast-message {
        font-size: 0.875em;
        color: rgba(0, 0, 0, 0.85);
      }
      .endpoint-message {
        display: block;
        font-weight: 500;
        margin-bottom: 0;
      }
      .error-message {
        display: block;
        color: rgba(0, 0, 0, 0.9);
        margin-top: 0.125rem;
      }
    }
  }
  hr {
    color: ${p => darken(0.15, p.largeIconColor)};
    border: 0;
    height: 1px;
    background: ${p => darken(0.25, p.largeIconColor)};
    background-image: ${p =>
      `linear-gradient(to right, ${darken(0.1, p.largeIconColor)}, ${darken(
        0.25,
        p.largeIconColor
      )}, ${darken(0.1, p.largeIconColor)})`};
    margin-top: 0.625rem;
    margin-right: 0.5rem;
  }
  button {
    margin-right: -1.5rem;
    padding-right: 0.75rem;
    padding-left: 0.75rem;
    svg {
      font-size: 1.25rem;
      color: ${p => darken(0.2, p.largeIconColor)};
    }
    :hover {
      svg {
        color: ${p => darken(0.325, p.largeIconColor)};
      }
    }
  }
`;

const contentAppear = keyframes`
  0% {
    opacity: 0;
    font-size: 0;
  }
  50% {
    opacity: 0;
    font-size: 1rem;
  }
  100% {
    opacity: 1;
  }
`;

const Content = styled.div`
  flex-grow: 1;
  font-size: 1rem;
  font-weight: 400;
  color: #000;
  margin-bottom: 0.5rem;
  animation: ${contentAppear} 0.3s;
  > .guidance-header {
    font-size: 1.125rem;
    margin-bottom: 0.75rem;
  }
  > p:first-of-type {
    margin-top: 0;
  }
  > *:not(:last-of-type) {
    margin-bottom: 0.625em;
  }
  > p:last-of-type {
    margin-bottom: 0;
  }
`;

function bgIconAppear(size) {
  return keyframes`
    0% {
      opacity: 0;
      font-size: 0;
    }
    50% {
      opacity: 0;
      font-size: ${
        size === "xs" ? "1.5rem" : size === "sm" ? "2.5rem" : "3.5rem"
      };
    }
    100% {
      opacity: 1;
    }
  `;
}

const LargeIconWrapper = styled.div`
  padding-right: 1.5rem;
  width: ${p =>
    p.bgIconSize === "xs" ? "3rem" : p.bgIconSize === "sm" ? "4rem" : "5rem"};
  flex-shrink: 0;
  > svg {
    font-size: ${p =>
      p.bgIconSize === "xs"
        ? "1.5rem"
        : p.bgIconSize === "sm"
        ? "2.5rem"
        : "3.5rem"};
    animation: ${p => bgIconAppear(p.bgIconSize)} 0.3s;
  }
`;

const Whiteout = styled.div`
  position: ${p => (p.isOpen ? "fixed" : "static")};
  z-index: ${p => (p.isOpen ? 1300 : -1)};
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;
